/**
 * Phase 4.12 — centralised error rendering for the Mini App.
 *
 * Single source of truth for the «what does this ``error_message``
 * mean to the operator?» mapping. Replaces the Phase 4.15
 * ``error_preview.ts`` text-only rewriter with a richer shape:
 * every recognised error produces a ``{ shortText, detailText,
 * category }`` triple. Surfaces:
 *
 *   * ``components/JobCard.tsx`` reads ``.shortText`` for the
 *     feed-card preview row (one line under the station code).
 *   * ``screens/DetailScreen.tsx`` reads the full triple inside
 *     ``ErrorStatusButton`` — the pulsing status badge expands a
 *     panel with the short text + a ``<details>``-wrapped
 *     technical-details block carrying ``.detailText``.
 *   * ``lib/error_preview.ts`` is now a thin shim that delegates
 *     to this module's ``renderError().shortText`` for the
 *     legacy single-string API its existing callers expect.
 *
 * Pattern-matching is case-insensitive substring lookup, applied
 * AFTER stripping the scheduler-internal ``[permanent]`` prefix
 * (Phase 1.10 marker). All branches go through the same prefix
 * strip so the technical-details text the operator sees doesn't
 * leak the marker either.
 *
 * The eight recognised categories cover every error_message the
 * backend writes today (Phases 4.1 – 4.15):
 *
 *   * ``rinex_not_available`` — fetcher couldn't find the RINEX
 *     file in any data centre.
 *   * ``plate_outside`` — PPP rejected the (station, model)
 *     pair via PlateModelOutsideError.
 *   * ``cascade_cancelled`` — Phase 4.10 sibling cancellation when
 *     another config of the same (station, model) hit plate-outside.
 *   * ``redundant_single_system`` — Phase 4.10 pre-flight gate:
 *     multi-system request, file carries only one of them.
 *   * ``compute_failed`` — PPP returned «Не удалось вычислить
 *     координаты» (high-latitude BDS-only et al.).
 *   * ``incompatible`` — pre-flight: none of the requested GNSS
 *     present in the RINEX header.
 *   * ``cancelled_by_substitution`` — Phase 4.15 sibling cancellation
 *     when a substitute station got picked.
 *   * ``unknown`` — fallback for future / one-off error shapes.
 *     ``shortText`` is the prefix-stripped message clipped to 80
 *     chars; ``detailText`` is the full cleaned text.
 */

export type ErrorCategory =
  | 'rinex_not_available'
  | 'plate_outside'
  | 'cascade_cancelled'
  | 'redundant_single_system'
  | 'compute_failed'
  | 'incompatible'
  | 'cancelled_by_substitution'
  | 'cached_unavailable'
  | 'retry_exhausted'
  | 'unknown';

export interface RenderedError {
  /** Short user-facing line (one phrase, no technical noise). Drives
   *  the JobCard preview row and the error-panel summary line. */
  shortText: string;
  /** Full operator-facing detail. Surfaced in the expandable
   *  «технические детали» block. Always has the ``[permanent]``
   *  marker stripped — that's a scheduler-internal classification
   *  the operator never needs to see. */
  detailText: string;
  category: ErrorCategory;
}

/** Strip the Phase 1.10 ``[permanent] `` marker if present, and trim
 *  resulting whitespace. Idempotent — passing a clean string returns
 *  it unchanged. */
function stripTechPrefix(raw: string): string {
  return raw.replace(/^\[permanent\]\s*/i, '').trim();
}

/** Main entry point. Returns ``null`` for an empty / missing message
 *  so callers can skip rendering the badge entirely instead of
 *  showing an empty panel. */
export function renderError(
  errorMessage: string | null | undefined,
): RenderedError | null {
  if (!errorMessage) return null;
  const lower = errorMessage.toLowerCase();
  const cleaned = stripTechPrefix(errorMessage);

  // Order matters: ``cascade-cancelled`` contains ``cancelled`` so
  // its branch MUST come before the Phase-4.15 cancelled-by-
  // substitution branch. Same for ``plate model outside`` vs.
  // generic «вне модели» — the cascade-cancelled branch already
  // covered the «-cancelled» case so an exact «plate model outside»
  // can't accidentally trip it.

  // 0. Cached unavailable (Phase 4.14 — pre-flight cached fail).
  //    Matched BEFORE the cause-of-original-failure branches because
  //    the error_message embeds the original status (e.g. «cached
  //    unavailable: plate_outside …») which would otherwise trip
  //    the plate-outside branch. The cache-unavailable branch is
  //    the right answer here: the user should see «закеширована
  //    как недоступная», not the underlying status — that's a
  //    historical artefact, not the immediate cause.
  if (lower.includes('cached unavailable:')) {
    // Pull the status sub-word for the detail surface so operators
    // still see what kind of failure originally landed the cache row.
    const statusMatch = cleaned.match(/cached unavailable:\s*(\S+)/i);
    const status = statusMatch ? statusMatch[1] : '';
    const firstSeenMatch = cleaned.match(/first seen (\d{4}-\d{2}-\d{2})/);
    const firstSeen = firstSeenMatch ? firstSeenMatch[1] : '';
    return {
      shortText: 'Закеширована как недоступная',
      detailText:
        'Эта (станция, дата, модель) ранее уже не прошла обработку и ' +
        'занесена в кэш недоступности — повторный запуск был ' +
        'предотвращён до отправки в PPP, экономя ~16 минут сервисного ' +
        'времени.\n\n' +
        (status ? `Тип отказа в кэше: ${status}.\n` : '') +
        (firstSeen ? `Впервые зафиксировано: ${firstSeen}.\n` : '') +
        '\nКэш автоматически очищается через 30 дней — за это время ' +
        'IGS может выложить недостающий файл или добавить покрытие ' +
        'нужной моделью тектоники.',
      category: 'cached_unavailable',
    };
  }

  // 0a. Retry exhausted (Phase 4.5 — auto-retry chain ran out of
  //     alternative models). The marker is stamped into
  //     ``Job.notes`` by the scheduler when
  //     ``_try_auto_retry_with_alternative_model`` returns None
  //     after walking the whole ``auto_retry_models`` list. The
  //     error_message itself is the originating ``plate model
  //     outside: …`` (so the next branch would also match) — we
  //     check both markers to surface the operator-relevant fact
  //     "we tried alternatives and they all failed".
  if (lower.includes('retry exhausted')) {
    return {
      shortText: 'Альтернативные модели исчерпаны',
      detailText:
        'Авто-повтор с альтернативными моделями тектоники не нашёл ни ' +
        'одной модели, которая бы дала результат — все кандидаты из ' +
        'выбранного списка либо уже были опробованы в этой цепочке, ' +
        'либо помечены в кэше как plate_outside для этой (станции, ' +
        'даты, GNSS-комбинации).\n\n' +
        cleaned,
      category: 'retry_exhausted',
    };
  }

  // 1. Plate outside (the original failing task — task-source).
  if (lower.includes('plate model outside') || lower.includes('вне плит')) {
    return {
      shortText: 'Станция вне модели тектоники',
      detailText:
        'Станция расположена в зоне, не покрытой моделью тектоники, ' +
        'используемой PPP-сервисом. Редукция координат для этой точки ' +
        'невозможна с текущей моделью.\n\n' +
        'В будущем планируется автоматический повтор обработки с ' +
        'другой моделью тектоники (Phase 4.5).',
      category: 'plate_outside',
    };
  }

  // 2. Cascade-cancelled (Phase 4.10 sibling). Tested BEFORE the
  //    Phase-4.15 cancelled-by-substitution branch — both
  //    contain ``cancelled`` but mean different things. The marker
  //    string is stable across the wire format.
  if (lower.includes('cascade-cancelled')) {
    const parentMatch = cleaned.match(/см\. задачу #(\d+)/);
    const parent = parentMatch ? `#${parentMatch[1]}` : '';
    const detailExtra = parent ? ` Связанная задача: ${parent}.` : '';
    return {
      shortText: 'Отменена (станция вне модели тектоники)',
      detailText:
        'Эта задача автоматически отменена потому что станция ' +
        'оказалась вне модели тектоники, используемой PPP-сервисом.' +
        detailExtra,
      category: 'cascade_cancelled',
    };
  }

  // 3. Redundant single-system (Phase 4.10 pre-flight gate).
  if (lower.includes('rinex redundant') || lower.includes('избыточ')) {
    return {
      shortText: 'Избыточный запрос (в файле только одна из систем)',
      detailText: cleaned,
      category: 'redundant_single_system',
    };
  }

  // 4. Cancelled by substitution (Phase 4.15 sibling).
  if (lower.includes('cancelled: station') && lower.includes('заменена')) {
    return {
      shortText: 'Отменена (станция заменена на другую)',
      detailText: cleaned,
      category: 'cancelled_by_substitution',
    };
  }

  // 5. RINEX not available — fetcher exhausted every data centre.
  if (
    lower.includes('rinex not available') ||
    lower.includes('rinex недоступен')
  ) {
    return {
      shortText: 'RINEX-файл наблюдений не найден в центрах данных',
      // Keep the full cleaned text — it includes the per-centre 404
      // breakdown which IS interesting in the expanded technical
      // panel (just not on the always-visible preview row).
      detailText: cleaned,
      category: 'rinex_not_available',
    };
  }

  // 6. Incompatible (pre-flight: zero of the requested GNSS present).
  if (
    lower.includes('rinex incompatible') ||
    lower.includes('в rinex-файле нет ни одной из запрошенных')
  ) {
    return {
      shortText: 'В файле нет запрошенных систем',
      detailText: cleaned,
      category: 'incompatible',
    };
  }

  // 7. Compute failed (PPP-side numerical failure).
  if (
    lower.includes('compute_failed') ||
    lower.includes('не удалось вычислить координаты')
  ) {
    return {
      shortText: 'PPP-сервису не удалось вычислить координаты',
      detailText: cleaned,
      category: 'compute_failed',
    };
  }

  // 8. Unknown — default. Short text is the prefix-stripped body
  //    clipped to 80 chars (with ellipsis) so a future error
  //    category still gives the operator SOMETHING to read on the
  //    feed card even without a dedicated branch.
  return {
    shortText:
      cleaned.length <= 80 ? cleaned : cleaned.slice(0, 80) + '…',
    detailText: cleaned,
    category: 'unknown',
  };
}
